import type { TvDetails } from '@/types/streaming';

export interface NextEpisode {
  season: number;
  episode: number;
}

/**
 * Compute the episode that follows (currentSeason, currentEpisode) for a TV
 * title, using the season list's declared `episodeCount`s.
 *
 *   - Within the current season: bump the episode number when there's room.
 *   - At a season boundary: roll into episode 1 of the next season if it exists
 *     and declares at least one episode.
 *   - Otherwise (last episode of the last season): null — there is no next.
 *
 * Extracted from `NextEpisodeButton` so the next-episode prefetch hook can
 * reuse the exact same resolution rather than reimplementing (and drifting
 * from) it.
 */
export function resolveNextEpisode(
  seasons: TvDetails['seasons'],
  currentSeason: number,
  currentEpisode: number,
): NextEpisode | null {
  const here = seasons.find((s) => s.seasonNumber === currentSeason);
  if (here && currentEpisode < here.episodeCount) {
    return { season: currentSeason, episode: currentEpisode + 1 };
  }
  const next = seasons.find((s) => s.seasonNumber === currentSeason + 1);
  if (next && next.episodeCount > 0) {
    return { season: currentSeason + 1, episode: 1 };
  }
  return null;
}
